export type AddressLocale = 'en' | 'de' | 'es' | 'fr' | 'it' | 'nl' | 'pl' | 'pt-BR' | 'pt-PT';

const SUPPORTED: AddressLocale[] = ['en', 'de', 'es', 'fr', 'it', 'nl', 'pl', 'pt-BR', 'pt-PT'];

export function resolveAddressLocale(locale?: string): AddressLocale {
  if (!locale) return 'en';
  const normalized = locale.trim().toLowerCase().replace(/_/g, '-');
  if (!normalized) return 'en';

  const exact = SUPPORTED.find(supported => supported.toLowerCase() === normalized);
  if (exact) return exact;

  const base = normalized.split('-')[0];
  if (base === 'pt') {
    // Brazil is the larger Portuguese-speaking market and the platform's
    // existing pt content is BR-flavored, so bare/unrecognized pt variants
    // (e.g. 'pt', 'pt-MZ') default here rather than to pt-PT.
    return 'pt-BR';
  }
  if ((SUPPORTED as string[]).includes(base)) return base as AddressLocale;

  return 'en';
}

export type AddressTranslationKey =
  | 'address.line1'
  | 'address.line2Optional'
  | 'locality.city'
  | 'locality.gbTownCity'
  | 'locality.district'
  | 'locality.auSuburbOrCity'
  | 'postalCode.usZip'
  | 'postalCode.generic'
  | 'postalCode.gbPostcode'
  | 'postalCode.inPin'
  | 'postalCode.ieEircode'
  | 'postalCode.brCep'
  | 'adminArea.state'
  | 'adminArea.province'
  | 'adminArea.jpPrefecture'
  | 'adminArea.region'
  | 'adminArea.twCountyOrCity'
  | 'adminArea.department'
  | 'adminArea.emirate'
  | 'adminArea.oblast'
  | 'adminArea.parish'
  | 'adminArea.island'
  | 'adminArea.area'
  | 'adminArea.auStateTerritory'
  | 'ui.country'
  | 'ui.fullName'
  | 'ui.phone'
  | 'ui.billingAddress'
  | 'ui.shippingAddress'
  | 'ui.billingSameAsShipping'
  | 'validation.billing'
  | 'validation.shipping'
  | 'validation.both';

const translations: Record<AddressLocale, Record<AddressTranslationKey, string>> = {
  en: {
    'address.line1': 'Address line 1',
    'address.line2Optional': 'Address line 2 (optional)',
    'locality.city': 'City',
    'locality.gbTownCity': 'Town/City',
    'locality.district': 'District',
    'locality.auSuburbOrCity': 'Suburb or city',
    'postalCode.usZip': 'ZIP',
    'postalCode.generic': 'Postal code',
    'postalCode.gbPostcode': 'Postcode',
    'postalCode.inPin': 'PIN code',
    'postalCode.ieEircode': 'Eircode',
    'postalCode.brCep': 'CEP',
    'adminArea.state': 'State',
    'adminArea.province': 'Province',
    'adminArea.jpPrefecture': 'Prefecture',
    'adminArea.region': 'Region',
    'adminArea.twCountyOrCity': 'County/City',
    'adminArea.department': 'Department',
    'adminArea.emirate': 'Emirate',
    'adminArea.oblast': 'Oblast',
    'adminArea.parish': 'Parish',
    'adminArea.island': 'Island',
    'adminArea.area': 'Area',
    'adminArea.auStateTerritory': 'State/Territory',
    'ui.country': 'Country',
    'ui.fullName': 'Full name',
    'ui.phone': 'Phone',
    'ui.billingAddress': 'Billing address',
    'ui.shippingAddress': 'Shipping address',
    'ui.billingSameAsShipping': 'Billing address is the same as shipping',
    'validation.billing': 'Please complete the billing address.',
    'validation.shipping': 'Please complete the shipping address.',
    'validation.both': 'Please complete the shipping and billing addresses.',
  },
  de: {
    'address.line1': 'Adresszeile 1',
    'address.line2Optional': 'Adresszeile 2 (optional)',
    'locality.city': 'Stadt',
    'locality.gbTownCity': 'Ort',
    'locality.district': 'Bezirk',
    'locality.auSuburbOrCity': 'Ort oder Vorort',
    'postalCode.usZip': 'PLZ',
    'postalCode.generic': 'Postleitzahl',
    'postalCode.gbPostcode': 'Postleitzahl',
    'postalCode.inPin': 'Postleitzahl (PIN)',
    'postalCode.ieEircode': 'Eircode',
    'postalCode.brCep': 'CEP',
    'adminArea.state': 'Bundesstaat',
    'adminArea.province': 'Provinz',
    'adminArea.jpPrefecture': 'Präfektur',
    'adminArea.region': 'Region',
    'adminArea.twCountyOrCity': 'Landkreis/Stadt',
    'adminArea.department': 'Departement',
    'adminArea.emirate': 'Emirat',
    'adminArea.oblast': 'Oblast',
    'adminArea.parish': 'Gemeinde',
    'adminArea.island': 'Insel',
    'adminArea.area': 'Gebiet',
    'adminArea.auStateTerritory': 'Bundesstaat/Territorium',
    'ui.country': 'Land',
    'ui.fullName': 'Vollständiger Name',
    'ui.phone': 'Telefon',
    'ui.billingAddress': 'Rechnungsadresse',
    'ui.shippingAddress': 'Lieferadresse',
    'ui.billingSameAsShipping': 'Rechnungsadresse entspricht der Lieferadresse',
    'validation.billing': 'Bitte vervollständigen Sie die Rechnungsadresse.',
    'validation.shipping': 'Bitte vervollständigen Sie die Lieferadresse.',
    'validation.both': 'Bitte vervollständigen Sie die Lieferadresse und die Rechnungsadresse.',
  },
  es: {
    'address.line1': 'Dirección, línea 1',
    'address.line2Optional': 'Dirección, línea 2 (opcional)',
    'locality.city': 'Ciudad',
    'locality.gbTownCity': 'Localidad',
    'locality.district': 'Distrito',
    'locality.auSuburbOrCity': 'Localidad o ciudad',
    'postalCode.usZip': 'Código postal',
    'postalCode.generic': 'Código postal',
    'postalCode.gbPostcode': 'Código postal',
    'postalCode.inPin': 'Código postal (PIN)',
    'postalCode.ieEircode': 'Eircode',
    'postalCode.brCep': 'CEP',
    'adminArea.state': 'Estado',
    'adminArea.province': 'Provincia',
    'adminArea.jpPrefecture': 'Prefectura',
    'adminArea.region': 'Región',
    'adminArea.twCountyOrCity': 'Condado/Ciudad',
    'adminArea.department': 'Departamento',
    'adminArea.emirate': 'Emirato',
    'adminArea.oblast': 'Oblast',
    'adminArea.parish': 'Parroquia',
    'adminArea.island': 'Isla',
    'adminArea.area': 'Área',
    'adminArea.auStateTerritory': 'Estado/Territorio',
    'ui.country': 'País',
    'ui.fullName': 'Nombre completo',
    'ui.phone': 'Teléfono',
    'ui.billingAddress': 'Dirección de facturación',
    'ui.shippingAddress': 'Dirección de envío',
    'ui.billingSameAsShipping': 'La dirección de facturación es la misma que la de envío',
    'validation.billing': 'Completa la dirección de facturación.',
    'validation.shipping': 'Completa la dirección de envío.',
    'validation.both': 'Completa las direcciones de envío y facturación.',
  },
  fr: {
    'address.line1': 'Ligne d’adresse 1',
    'address.line2Optional': 'Ligne d’adresse 2 (facultative)',
    'locality.city': 'Ville',
    'locality.gbTownCity': 'Ville',
    'locality.district': 'District',
    'locality.auSuburbOrCity': 'Localité ou ville',
    'postalCode.usZip': 'Code postal',
    'postalCode.generic': 'Code postal',
    'postalCode.gbPostcode': 'Code postal',
    'postalCode.inPin': 'Code postal (PIN)',
    'postalCode.ieEircode': 'Eircode',
    'postalCode.brCep': 'CEP',
    'adminArea.state': 'État',
    'adminArea.province': 'Province',
    'adminArea.jpPrefecture': 'Préfecture',
    'adminArea.region': 'Région',
    'adminArea.twCountyOrCity': 'Comté/Ville',
    'adminArea.department': 'Département',
    'adminArea.emirate': 'Émirat',
    'adminArea.oblast': 'Oblast',
    'adminArea.parish': 'Paroisse',
    'adminArea.island': 'Île',
    'adminArea.area': 'Zone',
    'adminArea.auStateTerritory': 'État/Territoire',
    'ui.country': 'Pays',
    'ui.fullName': 'Nom complet',
    'ui.phone': 'Téléphone',
    'ui.billingAddress': 'Adresse de facturation',
    'ui.shippingAddress': 'Adresse de livraison',
    'ui.billingSameAsShipping': "L'adresse de facturation est identique à l'adresse de livraison",
    'validation.billing': "Veuillez compléter l'adresse de facturation.",
    'validation.shipping': "Veuillez compléter l'adresse de livraison.",
    'validation.both': 'Veuillez compléter les adresses de livraison et de facturation.',
  },
  it: {
    'address.line1': 'Indirizzo, riga 1',
    'address.line2Optional': 'Indirizzo, riga 2 (facoltativo)',
    'locality.city': 'Città',
    'locality.gbTownCity': 'Località',
    'locality.district': 'Distretto',
    'locality.auSuburbOrCity': 'Località o città',
    'postalCode.usZip': 'Codice postale',
    'postalCode.generic': 'Codice postale',
    'postalCode.gbPostcode': 'Codice postale',
    'postalCode.inPin': 'Codice postale (PIN)',
    'postalCode.ieEircode': 'Eircode',
    'postalCode.brCep': 'CEP',
    'adminArea.state': 'Stato',
    'adminArea.province': 'Provincia',
    'adminArea.jpPrefecture': 'Prefettura',
    'adminArea.region': 'Regione',
    'adminArea.twCountyOrCity': 'Contea/Città',
    'adminArea.department': 'Dipartimento',
    'adminArea.emirate': 'Emirato',
    'adminArea.oblast': 'Oblast',
    'adminArea.parish': 'Parrocchia',
    'adminArea.island': 'Isola',
    'adminArea.area': 'Area',
    'adminArea.auStateTerritory': 'Stato/Territorio',
    'ui.country': 'Paese',
    'ui.fullName': 'Nome completo',
    'ui.phone': 'Telefono',
    'ui.billingAddress': 'Indirizzo di fatturazione',
    'ui.shippingAddress': 'Indirizzo di spedizione',
    'ui.billingSameAsShipping': "L'indirizzo di fatturazione coincide con quello di spedizione",
    'validation.billing': "Completa l'indirizzo di fatturazione.",
    'validation.shipping': "Completa l'indirizzo di spedizione.",
    'validation.both': 'Completa gli indirizzi di spedizione e di fatturazione.',
  },
  nl: {
    'address.line1': 'Adresregel 1',
    'address.line2Optional': 'Adresregel 2 (optioneel)',
    'locality.city': 'Plaats',
    'locality.gbTownCity': 'Plaats',
    'locality.district': 'District',
    'locality.auSuburbOrCity': 'Wijk of plaats',
    'postalCode.usZip': 'Postcode',
    'postalCode.generic': 'Postcode',
    'postalCode.gbPostcode': 'Postcode',
    'postalCode.inPin': 'Postcode (PIN)',
    'postalCode.ieEircode': 'Eircode',
    'postalCode.brCep': 'CEP',
    'adminArea.state': 'Staat',
    'adminArea.province': 'Provincie',
    'adminArea.jpPrefecture': 'Prefectuur',
    'adminArea.region': 'Regio',
    'adminArea.twCountyOrCity': 'Graafschap/Stad',
    'adminArea.department': 'Departement',
    'adminArea.emirate': 'Emiraat',
    'adminArea.oblast': 'Oblast',
    'adminArea.parish': 'Parochie',
    'adminArea.island': 'Eiland',
    'adminArea.area': 'Gebied',
    'adminArea.auStateTerritory': 'Staat/Territorium',
    'ui.country': 'Land',
    'ui.fullName': 'Volledige naam',
    'ui.phone': 'Telefoon',
    'ui.billingAddress': 'Factuuradres',
    'ui.shippingAddress': 'Bezorgadres',
    'ui.billingSameAsShipping': 'Factuuradres is gelijk aan het bezorgadres',
    'validation.billing': 'Vul het factuuradres in.',
    'validation.shipping': 'Vul het bezorgadres in.',
    'validation.both': 'Vul het bezorg- en factuuradres in.',
  },
  pl: {
    'address.line1': 'Adres, wiersz 1',
    'address.line2Optional': 'Adres, wiersz 2 (opcjonalnie)',
    'locality.city': 'Miasto',
    'locality.gbTownCity': 'Miejscowość',
    'locality.district': 'Dzielnica',
    'locality.auSuburbOrCity': 'Dzielnica lub miejscowość',
    'postalCode.usZip': 'Kod pocztowy',
    'postalCode.generic': 'Kod pocztowy',
    'postalCode.gbPostcode': 'Kod pocztowy',
    'postalCode.inPin': 'Kod pocztowy (PIN)',
    'postalCode.ieEircode': 'Eircode',
    'postalCode.brCep': 'CEP',
    'adminArea.state': 'Stan',
    'adminArea.province': 'Prowincja',
    'adminArea.jpPrefecture': 'Prefektura',
    'adminArea.region': 'Region',
    'adminArea.twCountyOrCity': 'Hrabstwo/Miasto',
    'adminArea.department': 'Departament',
    'adminArea.emirate': 'Emirat',
    'adminArea.oblast': 'Oblast',
    'adminArea.parish': 'Parafia',
    'adminArea.island': 'Wyspa',
    'adminArea.area': 'Obszar',
    'adminArea.auStateTerritory': 'Stan/Terytorium',
    'ui.country': 'Kraj',
    'ui.fullName': 'Imię i nazwisko',
    'ui.phone': 'Telefon',
    'ui.billingAddress': 'Adres rozliczeniowy',
    'ui.shippingAddress': 'Adres dostawy',
    'ui.billingSameAsShipping': 'Adres rozliczeniowy jest taki sam jak adres dostawy',
    'validation.billing': 'Uzupełnij adres rozliczeniowy.',
    'validation.shipping': 'Uzupełnij adres dostawy.',
    'validation.both': 'Uzupełnij adres dostawy i adres rozliczeniowy.',
  },
  'pt-BR': {
    'address.line1': 'Endereço, linha 1',
    'address.line2Optional': 'Endereço, linha 2 (opcional)',
    'locality.city': 'Cidade',
    'locality.gbTownCity': 'Cidade',
    'locality.district': 'Distrito',
    'locality.auSuburbOrCity': 'Bairro ou cidade',
    'postalCode.usZip': 'Código postal',
    'postalCode.generic': 'Código postal',
    'postalCode.gbPostcode': 'Código postal',
    'postalCode.inPin': 'Código postal (PIN)',
    'postalCode.ieEircode': 'Eircode',
    'postalCode.brCep': 'CEP',
    'adminArea.state': 'Estado',
    'adminArea.province': 'Província',
    'adminArea.jpPrefecture': 'Província',
    'adminArea.region': 'Região',
    'adminArea.twCountyOrCity': 'Condado/Cidade',
    'adminArea.department': 'Departamento',
    'adminArea.emirate': 'Emirado',
    'adminArea.oblast': 'Oblast',
    'adminArea.parish': 'Paróquia',
    'adminArea.island': 'Ilha',
    'adminArea.area': 'Área',
    'adminArea.auStateTerritory': 'Estado/Território',
    'ui.country': 'País',
    'ui.fullName': 'Nome completo',
    'ui.phone': 'Telefone',
    'ui.billingAddress': 'Endereço de cobrança',
    'ui.shippingAddress': 'Endereço de entrega',
    'ui.billingSameAsShipping': 'O endereço de cobrança é igual ao endereço de entrega',
    'validation.billing': 'Preencha o endereço de cobrança.',
    'validation.shipping': 'Preencha o endereço de entrega.',
    'validation.both': 'Preencha os endereços de entrega e cobrança.',
  },
  'pt-PT': {
    'address.line1': 'Morada, linha 1',
    'address.line2Optional': 'Morada, linha 2 (opcional)',
    'locality.city': 'Cidade',
    'locality.gbTownCity': 'Localidade',
    'locality.district': 'Distrito',
    'locality.auSuburbOrCity': 'Localidade ou cidade',
    'postalCode.usZip': 'Código postal',
    'postalCode.generic': 'Código postal',
    'postalCode.gbPostcode': 'Código postal',
    'postalCode.inPin': 'Código postal (PIN)',
    'postalCode.ieEircode': 'Eircode',
    'postalCode.brCep': 'CEP',
    'adminArea.state': 'Estado',
    'adminArea.province': 'Província',
    'adminArea.jpPrefecture': 'Província',
    'adminArea.region': 'Região',
    'adminArea.twCountyOrCity': 'Condado/Cidade',
    'adminArea.department': 'Departamento',
    'adminArea.emirate': 'Emirado',
    'adminArea.oblast': 'Oblast',
    'adminArea.parish': 'Freguesia',
    'adminArea.island': 'Ilha',
    'adminArea.area': 'Área',
    'adminArea.auStateTerritory': 'Estado/Território',
    'ui.country': 'País',
    'ui.fullName': 'Nome completo',
    'ui.phone': 'Telefone',
    'ui.billingAddress': 'Morada de faturação',
    'ui.shippingAddress': 'Morada de entrega',
    'ui.billingSameAsShipping': 'A morada de faturação é igual à morada de entrega',
    'validation.billing': 'Preencha a morada de faturação.',
    'validation.shipping': 'Preencha a morada de entrega.',
    'validation.both': 'Preencha as moradas de entrega e de faturação.',
  },
};

const LABEL_KEY_PREFIXES = ['address.', 'locality.', 'postalCode.', 'adminArea.'];

const isLabelKey = (key: AddressTranslationKey): boolean =>
  LABEL_KEY_PREFIXES.some(prefix => key.startsWith(prefix));

export const LABEL_TO_KEY: Record<string, AddressTranslationKey> = Object.fromEntries(
  (Object.entries(translations.en) as [AddressTranslationKey, string][])
    .filter(([key]) => isLabelKey(key))
    .map(([key, label]) => [label, key]),
);

export function translateAddressKey(
  locale: string | undefined,
  key: AddressTranslationKey,
): string {
  const resolved = resolveAddressLocale(locale);
  return translations[resolved][key];
}

const warnedUnknownLabels = new Set<string>();

export function translateAddressLabel(locale: string | undefined, englishLabel: string): string {
  const key = LABEL_TO_KEY[englishLabel];
  if (!key) {
    if (
      typeof process !== 'undefined' &&
      process.env.NODE_ENV !== 'production' &&
      !warnedUnknownLabels.has(englishLabel)
    ) {
      warnedUnknownLabels.add(englishLabel);
      console.warn(`translateAddressLabel: unknown address label "${englishLabel}"`);
    }
    return englishLabel;
  }
  return translateAddressKey(locale, key);
}
